import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { presupuestoService } from '@/services/presupuestoService';
import {
  Presupuesto,
  TipoMoneda,
  IniciarPresupuestoData,
  EditarMontoPresupuestoData,
  CobroFormData,
  TipoDocumento,
  PresupuestoEstado,
  PresupuestoMoneda,
} from '@/types/presupuesto';

/**
 * Calcular estado del presupuesto (totales, porcentajes, etc.)
 */
export const calcularEstadoPresupuesto = (
  presupuestoMoneda: PresupuestoMoneda | null
): PresupuestoEstado => {
  if (!presupuestoMoneda || !presupuestoMoneda.iniciado) {
    return {
      totalCobrado: 0,
      porcentajeCobrado: 0,
      saldoPendiente: 0,
      estaCompleto: false,
    };
  }

  const totalCobrado = presupuestoMoneda.cobros.reduce(
    (sum, cobro) => sum + cobro.monto,
    0
  );
  const porcentajeCobrado = presupuestoMoneda.montoTotal > 0
    ? (totalCobrado / presupuestoMoneda.montoTotal) * 100
    : 0;
  const saldoPendiente = presupuestoMoneda.montoTotal - totalCobrado;

  return {
    totalCobrado,
    porcentajeCobrado: Math.min(porcentajeCobrado, 100),
    saldoPendiente: Math.max(saldoPendiente, 0),
    estaCompleto: saldoPendiente <= 0,
  };
};

/**
 * Hook principal para gestionar presupuestos
 */
export const usePresupuesto = (tramiteId: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['presupuesto', tramiteId];

  // Query para obtener el presupuesto
  const {
    data: presupuesto,
    isLoading,
    error,
    refetch,
  } = useQuery<Presupuesto>({
    queryKey,
    queryFn: () => presupuestoService.getPresupuesto(tramiteId),
    staleTime: 1000 * 60 * 5, // 5 minutos
  });

  // Mutation para iniciar presupuesto
  const iniciarMutation = useMutation({
    mutationFn: ({
      moneda,
      data,
    }: {
      moneda: TipoMoneda;
      data: IniciarPresupuestoData;
    }) => presupuestoService.iniciarPresupuesto(tramiteId, moneda, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Mutation para registrar cobro
  const registrarCobroMutation = useMutation({
    mutationFn: ({
      moneda,
      data,
    }: {
      moneda: TipoMoneda;
      data: CobroFormData;
    }) => presupuestoService.registrarCobro(tramiteId, moneda, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Mutation para registrar cobro total
  const registrarCobroTotalMutation = useMutation({
    mutationFn: (moneda: TipoMoneda) =>
      presupuestoService.registrarCobroTotal(tramiteId, moneda),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Mutation para actualizar cobro
  const actualizarCobroMutation = useMutation({
    mutationFn: ({
      moneda,
      cobroId,
      data,
    }: {
      moneda: TipoMoneda;
      cobroId: string;
      data: CobroFormData;
    }) => presupuestoService.actualizarCobro(tramiteId, moneda, cobroId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Mutation para eliminar cobro
  const eliminarCobroMutation = useMutation({
    mutationFn: ({
      moneda,
      cobroId,
    }: {
      moneda: TipoMoneda;
      cobroId: string;
    }) => presupuestoService.eliminarCobro(tramiteId, moneda, cobroId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Mutation para cargar documento
  const cargarDocumentoMutation = useMutation({
    mutationFn: ({
      tipo,
      nombreCustom,
      file,
    }: {
      tipo: TipoDocumento;
      nombreCustom: string;
      file: File;
    }) => presupuestoService.cargarDocumento(tramiteId, tipo, nombreCustom, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Mutation para actualizar documento
  const actualizarDocumentoMutation = useMutation({
    mutationFn: ({
      documentoId,
      nombreCustom,
      tipo,
    }: {
      documentoId: string;
      nombreCustom: string;
      tipo: TipoDocumento;
    }) =>
      presupuestoService.actualizarDocumento(
        tramiteId,
        documentoId,
        nombreCustom,
        tipo
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Mutation para eliminar documento
  const eliminarDocumentoMutation = useMutation({
    mutationFn: (documentoId: string) =>
      presupuestoService.eliminarDocumento(tramiteId, documentoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Mutation para editar monto del presupuesto
  const editarMontoPresupuestoMutation = useMutation({
    mutationFn: ({
      moneda,
      data,
    }: {
      moneda: TipoMoneda;
      data: EditarMontoPresupuestoData;
    }) => presupuestoService.editarMontoPresupuesto(tramiteId, moneda, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Mutation para eliminar (resetear) presupuesto
  const eliminarPresupuestoMutation = useMutation({
    mutationFn: (moneda: TipoMoneda) =>
      presupuestoService.eliminarPresupuesto(tramiteId, moneda),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Calcular estados para cada moneda
  const estadoARS = calcularEstadoPresupuesto(presupuesto?.ars || null);
  const estadoUSD = calcularEstadoPresupuesto(presupuesto?.usd || null);

  return {
    // Data
    presupuesto,
    estadoARS,
    estadoUSD,

    // Loading states
    isLoading,
    error,

    // Mutations
    iniciarPresupuesto: iniciarMutation.mutateAsync,
    registrarCobro: registrarCobroMutation.mutateAsync,
    registrarCobroTotal: registrarCobroTotalMutation.mutateAsync,
    actualizarCobro: actualizarCobroMutation.mutateAsync,
    eliminarCobro: eliminarCobroMutation.mutateAsync,
    cargarDocumento: cargarDocumentoMutation.mutateAsync,
    actualizarDocumento: actualizarDocumentoMutation.mutateAsync,
    eliminarDocumento: eliminarDocumentoMutation.mutateAsync,
    editarMontoPresupuesto: editarMontoPresupuestoMutation.mutateAsync,
    eliminarPresupuesto: eliminarPresupuestoMutation.mutateAsync,

    // Mutation states
    isIniciando: iniciarMutation.isPending,
    isRegistrandoCobro: registrarCobroMutation.isPending,
    isActualizandoCobro: actualizarCobroMutation.isPending,
    isEliminandoCobro: eliminarCobroMutation.isPending,
    isCargandoDocumento: cargarDocumentoMutation.isPending,
    isActualizandoDocumento: actualizarDocumentoMutation.isPending,
    isEliminandoDocumento: eliminarDocumentoMutation.isPending,
    isEditandoMonto: editarMontoPresupuestoMutation.isPending,
    isEliminandoPresupuesto: eliminarPresupuestoMutation.isPending,

    // Utils
    refetch,
  };
};
